// R5 — first-party preference (as_pref) + recently viewed. Slugs and ids only: never a
// name, phone or email. localStorage is the source; a same-site cookie mirrors it.
import { track } from '../utils/analytics';
import { TYPE_LABEL, SINGULAR_TYPE, OCCASION_LABELS, AREA_BY_SLUG, AREA_SLUGS } from '../config/geo';

const KEY = 'as_pref';
const VIEWS = 'as_viewcats';
const RECENT = 'as_recent';
const MAX_AGE_S = 90 * 24 * 3600;
const BOT_RX = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|prerender|lighthouse|embedly|telegram|whatsapp/i;

export const isBot = () => typeof navigator !== 'undefined' && BOT_RX.test(navigator.userAgent || '');

const readJSON = (k, dflt) => {
  try { const v = JSON.parse(localStorage.getItem(k)); return v ?? dflt; } catch (e) { return dflt; }
};
const writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* ignore */ } };

const cookie = (value, maxAge) => {
  try {
    const secure = window.location.protocol === 'https:' ? '; secure' : '';
    document.cookie = `${KEY}=${value}; max-age=${maxAge}; path=/; samesite=lax${secure}`;
  } catch (e) { /* ignore */ }
};

export function readPref() {
  let p = readJSON(KEY, null);
  if (!p) {
    try {
      const m = document.cookie.match(/(?:^|; )as_pref=([^;]+)/);
      p = m ? JSON.parse(decodeURIComponent(m[1])) : null;
    } catch (e) { p = null; }
  }
  if (!p || typeof p !== 'object') return null;
  const age = (Date.now() - new Date(p.updated_at || 0).getTime()) / 1000;
  return age >= 0 && age < MAX_AGE_S ? p : null;
}

// Later signals overwrite the fields they carry; unknown values are dropped.
export function setPref(fields = {}) {
  if (isBot()) return;
  const clean = {};
  if (TYPE_LABEL[fields.category]) clean.category = fields.category;
  if (OCCASION_LABELS[fields.occasion]) clean.occasion = fields.occasion;
  if (AREA_BY_SLUG[fields.area]) clean.area = fields.area;
  if (!Object.keys(clean).length) return;
  const next = { ...(readPref() || {}), ...clean, updated_at: new Date().toISOString() };
  writeJSON(KEY, next);
  cookie(encodeURIComponent(JSON.stringify(next)), MAX_AGE_S);
}

export function clearPref() {
  try { localStorage.removeItem(KEY); localStorage.removeItem(VIEWS); } catch (e) { /* ignore */ }
  cookie('', 0);
  track('pref_cleared', {});
}

const TYPE_RX = [
  ['singers', /\bsingers?\b|vocal/i], ['djs', /\bdjs?\b/i], ['bands', /\bbands?\b/i],
  ['anchors', /\banchors?\b|emcee|\bhosts?\b|\bmc\b|compere/i], ['comedians', /comed/i],
  ['instrumentalists', /instrumental/i], ['dancers', /\bdanc/i], ['percussionists', /percussion|\bdhol|\btabla/i],
  ['magicians', /magic/i], ['orchestras', /orchestra|ensemble/i],
];
export const typeFromCategory = (cat) => (TYPE_RX.find(([, rx]) => rx.test(String(cat || ''))) || [])[0] || null;

const OCC_RX = [
  ['diwali-party', /diwali/], ['lohri', /lohri/], ['holi-party', /\bholi\b/], ['sangeet', /sangeet/],
  ['jagran-mata-ki-chowki', /jagran|chowki/], ['kitty-party', /kitty/], ['haldi-mehndi', /haldi|mehndi|mehendi/],
  ['anniversary', /anniversary/], ['baby-shower', /baby/], ['kids-birthday', /birthday/],
  ['bhajan-satsang', /bhajan|satsang/], ['house-party', /house party/], ['society-rwa', /society|\brwa\b/],
  ['corporate-annual-day', /corporate|annual day/], ['office', /office/], ['cocktail-party', /cocktail/],
  ['engagement-roka', /engagement|\broka\b/], ['wedding-reception', /reception/], ['new-year-party', /new year|newyear|\bnye\b/],
];

// Free text (search, utm_campaign, utm_content) -> {category?, occasion?}.
export function signalsFromText(text) {
  const t = String(text || '').toLowerCase().replace(/[_+-]+/g, ' ').trim();
  if (!t) return {};
  const out = {};
  const slug = t.replace(/\s+/g, '-');
  out.category = (TYPE_LABEL[slug] && slug) || SINGULAR_TYPE[slug] || typeFromCategory(t) || undefined;
  out.occasion = (OCCASION_LABELS[slug] && slug) || (OCC_RX.find(([, rx]) => rx.test(t)) || [])[0] || undefined;
  return out;
}

// Landing path -> {category?, occasion?, area?} (/delhi-ncr/band, /delhi-ncr/sangeet-in-noida, /delhi-ncr/sangeet/djs).
export function signalsFromPath(pathname) {
  const parts = String(pathname || '').split('/').filter(Boolean);
  if (parts[0] !== 'delhi-ncr' || !parts[1]) return {};
  const out = {};
  let seg = parts[1];
  const idx = seg.lastIndexOf('-in-');
  if (idx > 0 && AREA_SLUGS.includes(seg.slice(idx + 4))) { out.area = seg.slice(idx + 4); seg = seg.slice(0, idx); }
  const asType = (s) => (TYPE_LABEL[s] ? s : SINGULAR_TYPE[s]);
  if (asType(seg)) out.category = asType(seg);
  else if (OCCASION_LABELS[seg]) out.occasion = seg;
  if (parts[2] && asType(parts[2])) out.category = asType(parts[2]);
  return out;
}

export const readRecent = () => readJSON(RECENT, []).filter(Boolean).slice(0, 8);

// A profile view: recently-viewed list, and three different artists of one type -> preference.
export function recordView(artist) {
  const id = artist?.artist_id;
  if (!id || isBot()) return;
  writeJSON(RECENT, [id, ...readRecent().filter((x) => x !== id)].slice(0, 8));
  const t = typeFromCategory(artist.category || artist.artist_type);
  if (!t) return;
  const views = readJSON(VIEWS, {});
  views[t] = [id, ...(views[t] || []).filter((x) => x !== id)].slice(0, 10);
  writeJSON(VIEWS, views);
  if (views[t].length >= 3) setPref({ category: t });
}

// /post bucket ids -> type slug.
export const typeFromBucket = (b) => ({ anchor_host: 'anchors', percussionist: 'percussionists' }[b]
  || SINGULAR_TYPE[b] || null);
